import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import {
  fetchEventUrls, updateView, VIEWS
} from 'LiveBoard/modules/liveEvent'
import { ZoomMeeting, PreMeeting, LiveEventStream, Simulive } from './views'
import LiveEvent from './LiveEvent'
import * as attendActivityApi from 'LiveBoard/api/attendActivity'
import {
  ATTEND_ACTIVITY_TYPES, createUniqueId, EVENT_TYPES, DEFAULT_SCHEDULE_CONFIG
} from 'LiveBoard/components/shared/LiveEvent/AttendActivity/helpers'
import { CLOUDINARY_VIDEO_SOURCE_TYPE } from 'shared/constants'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import capitalize from 'lodash/capitalize'

const SECOND_IN_MILLISECONDS = 1000
const HOUR_IN_MILLISECONDS = 3600 * SECOND_IN_MILLISECONDS

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

class LiveEventContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      showZoomClient: true,
      hasAttendActivityOverlay: true
    }
  }

  componentDidMount() {
    const timeToMeeting = this.getTimeToMeeting()
    const view = this.meetingEnded()
      ? VIEWS.postMeeting
      : (timeToMeeting <= 0 ? VIEWS.meeting : VIEWS.preMeeting)

    window.addEventListener('beforeunload', this.closeLiveEvent)
    this.props.fetchEventUrls(this.props.boardId)
    this.props.updateView(view)

    if (view === VIEWS.preMeeting) {
      this.scheduleMeetingStarted(timeToMeeting)
    }
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.closeLiveEvent)
  }

  getEventTypeConfig = () => {
    const liveEventType = this.props.liveEventConfiguration.type

    const buttonsConfig = {
      openEmbeddedMeeting: {
        text: 'Join Live Event',
        onClick: this.handleJoinMeeting,
        className: 'rich-client-meeting-button',
        icon: ''
      },
      openRichClient: {
        text: `Open In ${capitalize(liveEventType)}`,
        onClick: this.useRichClient,
        className: 'embedded-meeting-button',
        icon: `icon-${liveEventType}`
      },
      leaveMeeting: {
        text: 'Close Live Event',
        onClick: this.handleLeaveMeeting,
        className: 'embedded-meeting-button',
        icon: ''
      }
    }

    const defaultConfig = {
      [VIEWS.preMeeting]: {
        renderComponent: this.renderPreMeeting,
        buttons: []
      },
      [VIEWS.postMeeting]: null,
      [VIEWS.noEmbeddedMeeting]: {
        renderComponent: () => null,
        buttons: [buttonsConfig.openRichClient]
      }
    }

    const eventTypeConfig = {
      [EVENT_TYPES.zoom]: {
        [VIEWS.meeting]: {
          renderComponent: this.renderZoomMeeting,
          buttons: [buttonsConfig.openRichClient]
        },
        [VIEWS.leftMeeting]: {
          renderComponent: () => null,
          buttons: [buttonsConfig.openRichClient, buttonsConfig.openEmbeddedMeeting]
        }
      },
      [EVENT_TYPES.stream]: {
        [VIEWS.meeting]: {
          renderComponent: this.renderStream,
          buttons: [buttonsConfig.leaveMeeting]
        },
        [VIEWS.leftMeeting]: {
          renderComponent: () => null,
          buttons: [buttonsConfig.openEmbeddedMeeting]
        }
      },
      [EVENT_TYPES.simulive]: {
        [VIEWS.meeting]: {
          renderComponent: this.renderSimulive,
          buttons: [buttonsConfig.leaveMeeting]
        },
        [VIEWS.leftMeeting]: {
          renderComponent: () => null,
          buttons: [buttonsConfig.openEmbeddedMeeting]
        }
      }
    }

    return {
      ...defaultConfig,
      ...eventTypeConfig[liveEventType]
    }
  }

  renderZoomMeeting = () => {
    if (this.props.isMobile) {
      return (
        <h1>
          For mobile experience, open the live event in zoom
        </h1>
      )
    } else if (isSafari) {
      return <></>
    } else if (!isEmpty(this.props.eventUrls)) {
      return (
        <ZoomMeeting
          config={this.props.liveEventConfiguration}
          eventUrls={this.props.eventUrls}
          onParticipantLeave={this.handleLeaveMeeting}
          onMeetingError={this.handleMeetingError}
          themeColorHex={this.props.themeColorHex}
          showZoomClient={this.state.showZoomClient}
          boardId={this.props.boardId}
          hasAttendActivityOverlay={this.state.hasAttendActivityOverlay} />
      )
    } else {
      return (<></>)
    }
  }

  renderStream = () => {
    const meetingUrl = this.getLiveEventConfiguration('config.meeting_url')
    return !isEmpty(this.props.eventUrls) && (
      <LiveEventStream
        url={meetingUrl}
        themeColorHex={this.props.themeColorHex}
        boardId={this.props.boardId}
        attendActivityConfig={this.props.liveEventConfiguration.attend_activity}
        hasAttendActivityOverlay={this.state.hasAttendActivityOverlay}
        scheduleConfig={this.getLiveEventConfiguration('config.schedule', DEFAULT_SCHEDULE_CONFIG)} />
    )
  }

  renderSimulive = () => {
    const scheduleConfig = this.getLiveEventConfiguration('config.schedule', DEFAULT_SCHEDULE_CONFIG)
    const startTimeOffset = get(scheduleConfig, 'is_enabled', false) ? this.getDistanceFromMeeting() : 0
    const videoId = this.getLiveEventConfiguration('config.meeting_file.public_id')
    const videoSourceConfig = {
      type: CLOUDINARY_VIDEO_SOURCE_TYPE.publicId,
      value: videoId,
      isOnDemand: this.getLiveEventConfiguration('config.on_demand', false),
      duration: this.getLiveEventConfiguration('config.meeting_file.duration', 0)
    }
    return !isEmpty(this.props.eventUrls) && videoId && (
      <Simulive
        videoSourceConfig={videoSourceConfig}
        startTimeOffset={startTimeOffset}
        closeVideo={this.handleCloseSection}
        scheduleConfig={scheduleConfig}
        themeColorHex={this.props.themeColorHex}
        boardId={this.props.boardId}
        attendActivityConfig={this.props.liveEventConfiguration.attend_activity}
        hasAttendActivityOverlay={this.state.hasAttendActivityOverlay} />
    )
  }

  renderPreMeeting = () => {
    const showCalendarLinks = !!this.props.liveEventConfiguration.config.schedule.is_enabled &&
      !!this.props.liveEventConfiguration.config.schedule.add_to_calendar

    return (
      <PreMeeting
        title={this.props.liveEventConfiguration.placeholder.title}
        eventStartTime={this.getStartTime()}
        themeColorHex={this.props.themeColorHex}
        showCalendarLinks={showCalendarLinks} />
    )
  }

  getStartTime = () => {
    const scheduleEnabled = this.getLiveEventConfiguration('config.schedule.is_enabled')
    const scheduledAt = scheduleEnabled && this.getLiveEventConfiguration('config.schedule.times.start_time.scheduled_at')

    return scheduledAt ? new Date(scheduledAt * SECOND_IN_MILLISECONDS) : new Date()
  }

  getDistanceFromMeeting = () => {
    return Math.abs(this.getTimeToMeeting() / SECOND_IN_MILLISECONDS)
  }

  getLiveEventConfiguration = (path, defaultValue) => {
    return get(this.props.liveEventConfiguration, path, defaultValue)
  }

  getTimeToMeeting = () => {
    return this.getStartTime() - Date.now()
  }

  scheduleMeetingStarted = (timeToMeeting) => {
    // setTimeout is limited in its time Values to 24 days
    if (timeToMeeting < HOUR_IN_MILLISECONDS) {
      setTimeout(this.handleJoinMeeting, timeToMeeting)
    }
  }

  getRichClientUrl = () => {
    const eventUrls = this.props.eventUrls
    return this.props.isMobile ? eventUrls.rich_client_mobile_url : eventUrls.rich_client_desktop_url
  }

  closeLiveEvent = () => {
    this.setState({ showZoomClient: false })
  }

  useRichClient = () => {
    attendActivityApi
      .attendActivity({
        boardId: this.props.boardId,
        attendanceGuid: createUniqueId(),
        activityType: ATTEND_ACTIVITY_TYPES.richClientZoom
      })
      .then(() => {
        this.closeLiveEvent()
        window.open(this.getRichClientUrl())
      })
  }

  handleCloseSection = () => {
    this.props.updateView(VIEWS.postMeeting)
  }

  handleJoinMeeting = () => {
    this.setState({ showZoomClient: true })
    this.props.updateView(VIEWS.meeting)
  }

  handleLeaveMeeting = () => {
    this.setState({ hasAttendActivityOverlay: false })
    this.props.updateView(VIEWS.leftMeeting)
  }

  handleMeetingError = () => {
    this.props.updateView(VIEWS.noEmbeddedMeeting)
  }

  meetingEnded = () => {
    const scheduleEnabled = get(this.props, 'liveEventConfiguration.config.schedule.is_enabled')
    const endTime = scheduleEnabled && get(this.props, 'liveEventConfiguration.config.schedule.times.end_time.scheduled_at')

    if (!endTime) {
      return false
    }
    const endDate = new Date(endTime * SECOND_IN_MILLISECONDS)

    return endDate < Date.now()
  }

  render() {
    const eventTypeConfig = this.getEventTypeConfig()
    const view = eventTypeConfig[this.props.view]

    return !!view && (
      <LiveEvent
        config={this.props.liveEventConfiguration}
        buttons={view.buttons}
        isItemView={this.props.isItemView}
        themeColorHex={this.props.themeColorHex} >
        {view.renderComponent()}
      </LiveEvent>
    )
  }
}

LiveEventContainer.propTypes = {
  boardId: PropTypes.number.isRequired,
  liveEventConfiguration: PropTypes.object.isRequired,
  isItemView: PropTypes.bool,
  isMobile: PropTypes.bool,
  themeColorHex: PropTypes.string,
  fetchEventUrls: PropTypes.func,
  updateView: PropTypes.func,
  eventUrls: PropTypes.object,
  view: PropTypes.string

}

LiveEventContainer.defaultProps = {
  isMobile: false
}

const mapStateToProps = ({ liveEvent: { eventUrls, view } }) => {
  return {
    eventUrls,
    view
  }
}

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({ fetchEventUrls, updateView }, dispatch)
}

export default connect(mapStateToProps, mapDispatchToProps)(LiveEventContainer)
